import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Filter, Edit, Trash2, ExternalLink, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { OutsourcedShow, OutsourcedShowFilters, PaginatedOutsourcedShowsResponse } from '../types/OutsourcedShow';
import { outsourcedShowService } from '../services/outsourcedShowService';
import { useAuth } from '../contexts/AuthContext';
import { FiltersDropdown } from '../components/FiltersDropdown';
import { EditShowDrawer } from '../components/EditShowDrawer';
import { ShowTransferDialog, ShowTransferData, TransferShowAdditionalFields } from '../components/ShowTransferDialog';
import { toast } from 'sonner';
import styles from '../styles/OutsourcedShowsScreen.module.css';

export function OutsourcedShowsScreen() {
  const { isAuth, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shows, setShows] = useState<OutsourcedShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedSource, setSelectedSource] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  
  // Edit drawer state
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [editingShow, setEditingShow] = useState<OutsourcedShow | null>(null);
  
  // Transfer dialog state
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [transferringShow, setTransferringShow] = useState<OutsourcedShow | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalShows, setTotalShows] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const PAGE_SIZE = 20;

  // Track previous filter values to detect changes
  const prevFilters = useRef({ searchTerm, selectedGenre, selectedSource, selectedStatus });
  const isFilterChange = useRef(false);

  const fetchOutsourcedShows = async (searchQuery = '', genreFilter = '', sourceFilter = '', statusFilter = '', page = currentPage) => {
    if (!isAuth) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);

      if (searchQuery.trim()) {
        console.log('🔍 Using search endpoint...');
        const response = await outsourcedShowService.searchOutsourcedShows(searchQuery);
        setShows(Array.isArray(response) ? response : []);
        setTotalShows(Array.isArray(response) ? response.length : 0);
      } else {
        const offset = (page - 1) * PAGE_SIZE;
        const filters: OutsourcedShowFilters = { limit: PAGE_SIZE, offset };
        if (genreFilter) filters.genre = genreFilter;
        if (sourceFilter) filters.source = sourceFilter;
        if (statusFilter !== '') filters.isSelected = statusFilter === 'true';

        const response: PaginatedOutsourcedShowsResponse = await outsourcedShowService.getOutsourcedShows(filters);

        
        // Extract data and pagination info from the new response structure
        setShows(response.data || []);
        setTotalShows(response.pagination?.totalItems || 0);
        setPageSize(response.pagination?.pageSize || PAGE_SIZE);
        // Always sync currentPage with what we requested
        setCurrentPage(page);
      }
      console.groupEnd();
    } catch (error) {
      console.groupEnd();
      toast.error('Failed to load shows. Please try again.');
      setShows([]);
      setTotalShows(0);
    } finally {
      setLoading(false);
    }
  };

  // Combined effect for both filters and pagination
  useEffect(() => {
    // Don't run effects if auth is still loading
    if (authLoading) return;

    const filtersChanged = (
      prevFilters.current.searchTerm !== searchTerm ||
      prevFilters.current.selectedGenre !== selectedGenre ||
      prevFilters.current.selectedSource !== selectedSource ||
      prevFilters.current.selectedStatus !== selectedStatus
    );

    if (filtersChanged) {
      isFilterChange.current = true;
      prevFilters.current = { searchTerm, selectedGenre, selectedSource, selectedStatus };
      
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        fetchOutsourcedShows(searchTerm, selectedGenre, selectedSource, selectedStatus, 1);
        isFilterChange.current = false;
      }, 500);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      // Just a page change
      fetchOutsourcedShows(searchTerm, selectedGenre, selectedSource, selectedStatus, currentPage);
    }
  }, [searchTerm, selectedGenre, selectedSource, selectedStatus, currentPage, authLoading, isAuth]);

  // Fetch shows on component mount
  useEffect(() => {
    // Don't fetch if auth is still loading
    if (!authLoading) {
      fetchOutsourcedShows('', '', '', '', 1);
    }
  }, [authLoading, isAuth]);

  // Handle URL parameter for auto-opening edit drawer
  useEffect(() => {
    const editShowId = searchParams.get('edit');
    if (editShowId && shows.length > 0 && !isEditDrawerOpen) {
      // Find the show to edit
      const showToEdit = shows.find(show => show.id === editShowId);
      if (showToEdit) {
        setEditingShow(showToEdit);
        setIsEditDrawerOpen(true);
        // Clear the URL parameter after opening the drawer
        searchParams.delete('edit');
        setSearchParams(searchParams);
      }
    }
  }, [searchParams, shows, isEditDrawerOpen, setSearchParams]);

  const handleDeleteShow = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this show?')) {
      return;
    }

    try {
      await outsourcedShowService.deleteOutsourcedShow(id);
      toast.success('Show deleted successfully');
      fetchOutsourcedShows(searchTerm, selectedGenre, selectedSource, selectedStatus, currentPage);
    } catch (error) {
      console.error('Error deleting show:', error);
      toast.error('Failed to delete show');
    }
  };

  const handleTransferShow = (show: OutsourcedShow) => {
    setTransferringShow(show);
    setIsTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (data: ShowTransferData, additionalFields: TransferShowAdditionalFields) => {
    if (!transferringShow) return;

    try {
      setTransferLoading(true);
      await outsourcedShowService.transferOutsourcedShowToMain(transferringShow.id, additionalFields);
      toast.success('Show transferred to main platform successfully');
      setIsTransferDialogOpen(false);
      setTransferringShow(null);
      fetchOutsourcedShows(searchTerm, selectedGenre, selectedSource, selectedStatus, currentPage);
    } catch (error) {
      console.error('Error transferring show:', error);
      toast.error('Failed to transfer show');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleTransferCancel = () => {
    setIsTransferDialogOpen(false);
    setTransferringShow(null);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalShows / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleEditShow = (show: OutsourcedShow) => {
    setEditingShow(show);
    setIsEditDrawerOpen(true);
  };

  const handleCloseEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditingShow(null);
  };

  const handleSaveShow = (savedShow: OutsourcedShow) => {
    setShows(prevShows => {
      const existingShowIndex = prevShows.findIndex(show => show.id === savedShow.id);
      if (existingShowIndex >= 0) {
        // Update existing show
        return prevShows.map(show => 
          show.id === savedShow.id ? savedShow : show
        );
      } else {
        // For new shows, refetch all data to ensure proper display
        fetchOutsourcedShows();
        return prevShows;
      }
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleGenreChange = (value: string) => {
    setSelectedGenre(value);
  };

  const handleSourceChange = (value: string) => {
    setSelectedSource(value);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenre('');
    setSelectedSource('');
    setSelectedStatus('');
  };

  const getStatusBadge = (show: OutsourcedShow) => {
    if (show.mainShowId) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800">Transferred</Badge>;
    }
    return show.isSelected ? 
      <Badge variant="default" className="bg-blue-100 text-blue-800">Selected</Badge> : 
      <Badge variant="outline">Not Selected</Badge>;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Outsourced Shows</h1>
          <Button
            onClick={() => {
              setEditingShow(null); // Set to null for create mode
              setIsEditDrawerOpen(true);
            }}
            className={styles.addButton}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Show
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className={styles.searchSection}>
        <div className={styles.searchBar}>
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search shows by name..."
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <div className="flex gap-2 items-center">
          <Button 
            variant="outline" 
            className={styles.filterButton}
            onClick={clearFilters}
            disabled={!searchTerm && !selectedGenre && !selectedSource && !selectedStatus}
          >
            Clear Filters
          </Button>
          <FiltersDropdown 
            selectedGenre={selectedGenre}
            selectedSource={selectedSource}
            selectedStatus={selectedStatus}
            onGenreChange={handleGenreChange}
            onSourceChange={handleSourceChange}
            onStatusChange={handleStatusChange}
          />
        </div>
      </div>

      {/* Shows Grid */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}>Loading...</div>
          </div>
        ) : shows.length === 0 ? (
          <Card className={styles.emptyState}>
            <CardContent className="p-8 text-center">
              <div className="text-muted-foreground mb-4">No outsourced shows found</div>
              <Button onClick={() => {
                setEditingShow(null); // Set to null for create mode
                setIsEditDrawerOpen(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Show
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.showsGrid}>
            {shows.map((show) => (
              <Card key={show.id} className={styles.showCard}>
                <CardHeader className="pb-2">
                  <div className={styles.cardHeader}>
                    <CardTitle className={styles.showTitle}>{show.ogShowName}</CardTitle>
                    {getStatusBadge(show)}
                  </div>
                  <div className={styles.showMeta}>
                    <Badge variant="outline" className={styles.genreBadge}>
                      {show.genre}
                    </Badge>
                    {show.source && (
                      <Badge variant="outline" className={styles.sourceBadge}>
                        {show.source}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className={styles.showDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Episodes:</span>
                      <span className={styles.detailValue}>{show.episodes}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Source:</span>
                      <span className={styles.detailValue}>{show.source || '-'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className={styles.actions}>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEditShow(show)}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      {show.showLink && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(show.showLink, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    {!show.mainShowId && (
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => handleTransferShow(show)}
                        className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <ArrowRight className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && shows.length > 0 && !searchTerm && (
          <div className="flex justify-between items-center mt-6 p-4 bg-card rounded-lg border">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalShows)} of {totalShows} shows
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="h-8 px-3"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <div className="text-sm text-muted-foreground px-2">
                Page {currentPage} of {Math.ceil(totalShows / pageSize)}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={currentPage >= Math.ceil(totalShows / pageSize)}
                className="h-8 px-3"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Edit Drawer */}
        <EditShowDrawer 
          isOpen={isEditDrawerOpen}
          onClose={handleCloseEditDrawer}
          show={editingShow}
          onSave={handleSaveShow}
        />

        {/* Transfer Dialog */}
        {transferringShow && (
          <ShowTransferDialog
            isOpen={isTransferDialogOpen}
            show={transferringShow}
            onClose={handleTransferCancel}
            onSubmit={handleTransferSubmit}
            loading={transferLoading}
          />
        )}
      </div>
    </div>
  );
}